import { isSuccess } from '@devexperts/remote-data-ts'
import { Button, cn } from '@spiaggeit/spit-ui'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@/assets/icons'
import { ButtonLink } from '@/components/ButtonLink'
import { KioskTimeoutDialog } from '@/components/Dialog/KioskTimeoutDialog'
import { DownloadConfirmationButton } from '@/components/DownloadConfirmationButton'
import { GridWithRecap } from '@/components/GridWithRecap'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { INSERT_PERIOD_PATH } from '@/app/router/paths.ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { useAppPath } from '@/hooks/useAppPath.ts'
import { useBackGuard } from '@/hooks/useBackGuard'
import { useCartRecapData } from '@/hooks/useCartRecapData'
import { useHostProfileUrl } from '@/hooks/useHostProfileUrl.ts'
import { useWhiteLabelBgTextClassName } from '@/hooks/useWhiteLabelBgTextClassName.ts'
import { ThankYouCards } from '@/routes/ThankYou/Cards'
import { ThankYouRecap } from '@/routes/ThankYou/Recap'
import {
  isAppSelector,
  isKioskSelector,
  isWebsiteSelector,
  isWidgetSelector,
} from '@/store/appSlice'
import { bookingAvailabilitySlice } from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice'
import { selectTotal } from '@/store/cartSlice/selectors/selectTotalPrice'
import { extraSlice } from '@/store/extraSlice'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { kioskSlice } from '@/store/kioskSlice'
import { licenseSlice } from '@/store/licenseSlice'
import {
  buildGaLineItemsFromCartDetail,
  GTM_CURRENCY_CODE,
  gtmPurchaseSentStorageKey,
  pushPurchaseDataLayerEvent,
  readGtmPaymentMethodFromSession,
} from '@/utils/analytics/ecommerceDataLayer'
import {
  ONE_SECOND,
  PAY_QUERY_PARAM_NAME,
  REG_ID_QUERY_PARAM_NAME,
} from '@/utils/constants.ts'

const TEN_SECONDS = ONE_SECOND * 10

export const ThankYou = () => {
  useBackGuard()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const reservationId = searchParams.get(REG_ID_QUERY_PARAM_NAME)
  const insertPeriodPath = useAppPath(INSERT_PERIOD_PATH)
  const navigate = useNavigate()
  const paymentLinkId = searchParams.get(PAY_QUERY_PARAM_NAME)
  const isKiosk = useAppSelector(isKioskSelector)
  const isAppMode = useAppSelector(isAppSelector)
  const isWebsiteMode = useAppSelector(isWebsiteSelector)
  const isWidgetMode = useAppSelector(isWidgetSelector)
  const cartDetail = useAppSelector(cartSlice.selectors.cartDetail)
  const license = useAppSelector(licenseSlice.selectors.license)
  const bookingAvailability = useAppSelector(
    bookingAvailabilitySlice.selectors.self
  )
  const weatherInsurancePrice = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )
  const total = useAppSelector((state) => selectTotal(state, true))
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const { deposit } = useCartRecapData()
  const headingClassName = useWhiteLabelBgTextClassName(
    cn('mb-2 text-xl font-bold', { 'mb-0': isKiosk })
  )
  const paragraphClassName = useWhiteLabelBgTextClassName('mb-6 lg:mb-8')

  const { hostUrl, profileUrl } = useHostProfileUrl()
  useEffect(() => {
    dispatch(cartSlice.actions.setIsCartPaid(true))

    if (isKiosk) {
      dispatch(kioskSlice.actions.resetKiosk())
    }

    if (reservationId) {
      if (isAppMode) {
        window.ReactNativeWebView.postMessage('{"event":"reservation-booking"}')
      }

      dispatch(
        cartSlice.actions.getCartDetail({
          reservationId,
          ...(typeof paymentLinkId === 'string'
            ? {
                paymentLinkId,
              }
            : {}),
        })
      )
      return
    }

    navigate(insertPeriodPath)
  }, [isKiosk, reservationId, isAppMode, insertPeriodPath, dispatch, navigate])

  useEffect(() => {
    if (!isSuccess(cartDetail)) return

    const cart = cartDetail.value.cart
    const order = cart.order
    const dedupKey = gtmPurchaseSentStorageKey(order)
    if (sessionStorage.getItem(dedupKey)) return

    const weatherInsurable =
      isSuccess(weatherInsurancePrice) && weatherInsurancePrice.value > 0
    const items = buildGaLineItemsFromCartDetail({
      bookingAvailability,
      cart,
      license,
      weatherInsurable,
    })

    const totalDiscountValue = cart.totalDiscountValue

    pushPurchaseDataLayerEvent({
      ecommerce: {
        ...(totalDiscountValue != null && totalDiscountValue !== 0
          ? { coupon: String(totalDiscountValue) }
          : {}),
        currency: GTM_CURRENCY_CODE,
        items,
        payment_method: readGtmPaymentMethodFromSession(),
        tax: 0,
        transaction_id: String(order),
        value: cart.totalPrice,
      },
      paymentInfo: {
        cart,
        dayDifference,
        deposit,
        total,
      },
      reservationModel: cart.reservationModels[0],
    })
    sessionStorage.setItem(dedupKey, '1')
  }, [
    cartDetail,
    license,
    bookingAvailability,
    weatherInsurancePrice,
    dayDifference,
    deposit,
    total,
  ])

  if (!isSuccess(cartDetail)) {
    return <LoaderTrigger />
  }

  const [{ first_name: userName }] = cartDetail.value.cart.reservationModels

  const getHeading = () => {
    if (isKiosk) {
      return t('kiosk.payment.printDialog.title')
    }

    if (
      cartDetail.value.paymentLink &&
      cartDetail.value.paymentLink.amount !== total
    ) {
      return t('thankYou.depositTitle', { userName })
    }

    return t('thankYou.title', { userName })
  }

  return (
    <>
      <div className="container grow py-4 lg:py-8">
        {userName && <h1 className={headingClassName}>{getHeading()}</h1>}

        {isWidgetMode ? (
          <p className={paragraphClassName}>
            {t('thankYou.widgetDescription')}
          </p>
        ) : null}

        {isWebsiteMode || isAppMode ? (
          <p className={paragraphClassName}>
            <Trans i18nKey="thankYou.portalDescription">
              <Link className="text-link" to={profileUrl} />
            </Trans>
          </p>
        ) : null}

        <div className={cn('flex flex-col gap-4', { 'mt-6': isKiosk })}>
          <GridWithRecap className="lg:mt-0">
            <GridWithRecap.ContentContainer
              className={cn('gap-4', { 'col-span-3 lg:col-span-3': isKiosk })}
            >
              <ThankYouRecap />

              <div className="flex flex-col gap-4 lg:flex-row">
                {!isKiosk && <DownloadConfirmationButton variant="outline" />}

                {isWebsiteMode && (
                  <ButtonLink
                    className="w-full"
                    icon="HomeIcon"
                    iconOnLeftSide
                    label={t('thankYou.backToHome.website')}
                    to={hostUrl}
                  />
                )}

                {isAppMode && (
                  <Button
                    className="w-full"
                    onClick={() => {
                      window.ReactNativeWebView.postMessage(
                        '{"event":"go-to-page","page":"reservations"}'
                      )
                    }}
                  >
                    <Icon className="size-5" name="HomeIcon" />
                    {t('thankYou.backToHome.website')}
                  </Button>
                )}
              </div>
            </GridWithRecap.ContentContainer>

            <GridWithRecap.RecapContainer>
              <ThankYouCards />
            </GridWithRecap.RecapContainer>
          </GridWithRecap>
        </div>
      </div>
      <KioskTimeoutDialog timeout={TEN_SECONDS} />
    </>
  )
}
